import { useState, useEffect, type Dispatch, type SetStateAction } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Loader2,
  Mail,
  Phone,
  CheckCircle2,
  ArrowLeft,
  RefreshCw,
  Building2,
  AlertCircle,
  Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { publicVerificationApi } from '../services/auth.service';
import { toast } from 'sonner';
import type { VerificationRequiredResponse, VerificationResult } from '../types/auth.types';

type VerificationStep = 'companyEmail' | 'email' | 'phone';
type StepStatusTone = 'error' | 'success' | 'info';

interface StepStatus {
  tone: StepStatusTone;
  message: string;
}

type StepStatusMap = Record<VerificationStep, StepStatus | null>;

const INITIAL_STEP_STATUS: StepStatusMap = {
  companyEmail: null,
  email: null,
  phone: null,
};

interface VerificationFormProps {
  verificationData: VerificationRequiredResponse;
  email: string;
  onVerificationComplete: () => void;
  onBack: () => void;
}

function getErrorMessage(error: unknown, fallback: string): string {
  return getApiErrorDetails(error, fallback).message;
}

function getApiErrorDetails(
  error: unknown,
  fallback: string,
): { message: string; retryAfterSeconds?: number } {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return { message: fallback };
  }

  const response = error.response as { data?: { message?: unknown } } | undefined;
  const data = response?.data as { message?: unknown; retryAfterSeconds?: unknown } | undefined;
  const message = data?.message;
  const retryAfterSeconds =
    typeof data?.retryAfterSeconds === 'number' ? data.retryAfterSeconds : undefined;

  if (typeof message === 'string') {
    return { message, retryAfterSeconds };
  }

  if (Array.isArray(message) && message.every((item) => typeof item === 'string')) {
    return { message: message.join(', '), retryAfterSeconds };
  }

  return { message: fallback, retryAfterSeconds };
}

function getResendCooldownSeconds(data: VerificationResult, fallbackSeconds = 60): number {
  const cooldown = data.retryAfterSeconds ?? fallbackSeconds;
  return Math.max(0, Math.ceil(cooldown));
}

function applyRetryCooldown(
  data: VerificationResult,
  setCooldown: Dispatch<SetStateAction<number>>,
): void {
  if (data.retryAfterSeconds && data.retryAfterSeconds > 0) {
    setCooldown(getResendCooldownSeconds(data, 0));
  }
}

function formatExpiryTime(expiresAt?: string): string | null {
  if (!expiresAt) {
    return null;
  }

  const date = new Date(expiresAt);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function getResendSuccessMessage(
  destination: string | undefined,
  data: VerificationResult,
): string {
  const target = destination || 'the registered destination';
  const expiry = formatExpiryTime(data.expiresAt);
  const base = `A new verification code was sent to ${target}.`;
  return expiry ? `${base} It expires at ${expiry}.` : base;
}

function getResendFailureMessage(data: VerificationResult, fallback: string): string {
  const message = data.message || fallback;
  const retryAfterSeconds = data.retryAfterSeconds ? getResendCooldownSeconds(data, 0) : 0;

  if (retryAfterSeconds <= 0 || /try again|second|minute|later/i.test(message)) {
    return message;
  }

  return `${message} Try again in ${retryAfterSeconds} seconds.`;
}

function StepStatusMessage({ id, status }: { id: string; status: StepStatus | null }) {
  if (!status) {
    return null;
  }

  const isError = status.tone === 'error';
  const isSuccess = status.tone === 'success';
  const Icon = isError ? AlertCircle : isSuccess ? CheckCircle2 : Info;

  return (
    <Alert
      id={id}
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
      variant={isError ? 'destructive' : 'default'}
      className={
        isError
          ? 'bg-destructive/5'
          : isSuccess
            ? 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-100 [&>svg]:text-emerald-600 dark:[&>svg]:text-emerald-300'
            : 'border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-100 [&>svg]:text-blue-600 dark:[&>svg]:text-blue-300'
      }
    >
      <Icon className="h-4 w-4" />
      <AlertDescription>{status.message}</AlertDescription>
    </Alert>
  );
}

export function VerificationForm({
  verificationData,
  email,
  onVerificationComplete,
  onBack,
}: VerificationFormProps) {
  const tenantSlug = verificationData.tenant.slug;
  const {
    emailVerified: initialEmailVerified,
    companyEmailVerified: initialCompanyEmailVerified,
    phoneVerified: initialPhoneVerified,
    maskedEmail,
    maskedCompanyEmail,
    maskedPhone,
  } = verificationData.verificationStatus;

  const [emailVerified, setEmailVerified] = useState(initialEmailVerified);
  const [companyEmailVerified, setCompanyEmailVerified] = useState(initialCompanyEmailVerified);
  const [phoneVerified, setPhoneVerified] = useState(initialPhoneVerified);
  const [emailCode, setEmailCode] = useState('');
  const [companyEmailCode, setCompanyEmailCode] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [emailResendCooldown, setEmailResendCooldown] = useState(0);
  const [companyEmailResendCooldown, setCompanyEmailResendCooldown] = useState(0);
  const [phoneResendCooldown, setPhoneResendCooldown] = useState(0);
  const [stepStatus, setStepStatus] = useState<StepStatusMap>(INITIAL_STEP_STATUS);
  const requiresPhoneVerification = Boolean(maskedPhone);
  const companyEmailStatusId = 'company-email-verification-status';
  const emailStatusId = 'admin-email-verification-status';
  const phoneStatusId = 'phone-verification-status';

  const setStatus = (step: VerificationStep, status: StepStatus | null): void => {
    setStepStatus((current) => ({ ...current, [step]: status }));
  };

  const handleCodeChange = (
    step: VerificationStep,
    value: string,
    setCode: Dispatch<SetStateAction<string>>,
  ): void => {
    setCode(value.replace(/\D/g, '').slice(0, 6));
    if (stepStatus[step]) {
      setStatus(step, null);
    }
  };

  const hasStepError = (step: VerificationStep): boolean => stepStatus[step]?.tone === 'error';

  // Cooldown timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setEmailResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
      setCompanyEmailResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
      setPhoneResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Check if all required verifications complete
  useEffect(() => {
    if (companyEmailVerified && emailVerified && (!requiresPhoneVerification || phoneVerified)) {
      toast.success('Verification Complete!', {
        description: 'You can now log in to your account.',
      });
      // Small delay to show success message
      setTimeout(() => {
        onVerificationComplete();
      }, 1500);
    }
  }, [
    companyEmailVerified,
    emailVerified,
    phoneVerified,
    requiresPhoneVerification,
    onVerificationComplete,
  ]);

  // Verify company email mutation
  const verifyCompanyEmailMutation = useMutation({
    mutationFn: () =>
      publicVerificationApi.verifyCompanyEmail({
        email,
        tenantSlug,
        code: companyEmailCode,
      }),
    onSuccess: (data) => {
      if (data.success) {
        setCompanyEmailVerified(true);
        setCompanyEmailCode('');
        setStatus('companyEmail', {
          tone: 'success',
          message: 'Company email verified successfully.',
        });
        toast.success('Company Email Verified!', {
          description: 'Your company email has been verified successfully.',
        });
      } else {
        const message = data.message || 'Invalid or expired code.';
        setStatus('companyEmail', { tone: 'error', message });
        toast.error('Verification Failed', {
          description: message,
        });
      }
    },
    onError: (error: unknown) => {
      const message = getErrorMessage(error, 'Failed to verify company email. Please try again.');
      setStatus('companyEmail', { tone: 'error', message });
      toast.error('Verification Failed', { description: message });
    },
  });

  // Verify email mutation
  const verifyEmailMutation = useMutation({
    mutationFn: () =>
      publicVerificationApi.verifyEmail({
        email,
        tenantSlug,
        code: emailCode,
      }),
    onSuccess: (data) => {
      if (data.success) {
        setEmailVerified(true);
        setEmailCode('');
        setStatus('email', {
          tone: 'success',
          message: 'Admin email verified successfully.',
        });
        toast.success('Email Verified!', {
          description: 'Your email has been verified successfully.',
        });
      } else {
        const message = data.message || 'Invalid or expired code.';
        setStatus('email', { tone: 'error', message });
        toast.error('Verification Failed', {
          description: message,
        });
      }
    },
    onError: (error: unknown) => {
      const message = getErrorMessage(error, 'Failed to verify email. Please try again.');
      setStatus('email', { tone: 'error', message });
      toast.error('Verification Failed', { description: message });
    },
  });

  // Verify phone mutation
  const verifyPhoneMutation = useMutation({
    mutationFn: () =>
      publicVerificationApi.verifyPhone({
        email,
        tenantSlug,
        code: phoneCode,
      }),
    onSuccess: (data) => {
      if (data.success) {
        setPhoneVerified(true);
        setPhoneCode('');
        setStatus('phone', {
          tone: 'success',
          message: 'Phone verified successfully.',
        });
        toast.success('Phone Verified!', {
          description: 'Your phone has been verified successfully.',
        });
      } else {
        const message = data.message || 'Invalid or expired code.';
        setStatus('phone', { tone: 'error', message });
        toast.error('Verification Failed', {
          description: message,
        });
      }
    },
    onError: (error: unknown) => {
      const message = getErrorMessage(error, 'Failed to verify phone. Please try again.');
      setStatus('phone', { tone: 'error', message });
      toast.error('Verification Failed', { description: message });
    },
  });

  // Resend email OTP mutation
  const resendEmailMutation = useMutation({
    mutationFn: () =>
      publicVerificationApi.resendEmailOtp({
        email,
        tenantSlug,
      }),
    onSuccess: (data) => {
      if (data.success) {
        setEmailResendCooldown(getResendCooldownSeconds(data));
        const message = getResendSuccessMessage(maskedEmail, data);
        setEmailCode('');
        setStatus('email', { tone: 'success', message });
        toast.success('Code Sent!', {
          description: `A new verification code has been sent to ${maskedEmail}`,
        });
      } else {
        applyRetryCooldown(data, setEmailResendCooldown);
        const message = getResendFailureMessage(data, 'Please try again later.');
        setStatus('email', { tone: 'error', message });
        toast.error('Failed to Resend', {
          description: message,
        });
      }
    },
    onError: (error: unknown) => {
      const { message, retryAfterSeconds } = getApiErrorDetails(
        error,
        'Failed to resend code. Please try again.',
      );
      if (retryAfterSeconds && retryAfterSeconds > 0) {
        setEmailResendCooldown(Math.ceil(retryAfterSeconds));
      }
      setStatus('email', { tone: 'error', message });
      toast.error('Failed to Resend', { description: message });
    },
  });

  // Resend company email OTP mutation
  const resendCompanyEmailMutation = useMutation({
    mutationFn: () =>
      publicVerificationApi.resendCompanyEmailOtp({
        email,
        tenantSlug,
      }),
    onSuccess: (data) => {
      if (data.success) {
        setCompanyEmailResendCooldown(getResendCooldownSeconds(data));
        const message = getResendSuccessMessage(maskedCompanyEmail, data);
        setCompanyEmailCode('');
        setStatus('companyEmail', { tone: 'success', message });
        toast.success('Code Sent!', {
          description: `A new verification code has been sent to ${maskedCompanyEmail}`,
        });
      } else {
        applyRetryCooldown(data, setCompanyEmailResendCooldown);
        const message = getResendFailureMessage(data, 'Please try again later.');
        setStatus('companyEmail', { tone: 'error', message });
        toast.error('Failed to Resend', {
          description: message,
        });
      }
    },
    onError: (error: unknown) => {
      const { message, retryAfterSeconds } = getApiErrorDetails(
        error,
        'Failed to resend company email code. Please try again.',
      );
      if (retryAfterSeconds && retryAfterSeconds > 0) {
        setCompanyEmailResendCooldown(Math.ceil(retryAfterSeconds));
      }
      setStatus('companyEmail', { tone: 'error', message });
      toast.error('Failed to Resend', { description: message });
    },
  });

  // Resend phone OTP mutation
  const resendPhoneMutation = useMutation({
    mutationFn: () =>
      publicVerificationApi.resendPhoneOtp({
        email,
        tenantSlug,
      }),
    onSuccess: (data) => {
      if (data.success) {
        setPhoneResendCooldown(getResendCooldownSeconds(data));
        const message = getResendSuccessMessage(maskedPhone, data);
        setPhoneCode('');
        setStatus('phone', { tone: 'success', message });
        toast.success('Code Sent!', {
          description: `A new verification code has been sent to ${maskedPhone}`,
        });
      } else {
        applyRetryCooldown(data, setPhoneResendCooldown);
        const message = getResendFailureMessage(data, 'Please try again later.');
        setStatus('phone', { tone: 'error', message });
        toast.error('Failed to Resend', {
          description: message,
        });
      }
    },
    onError: (error: unknown) => {
      const { message, retryAfterSeconds } = getApiErrorDetails(
        error,
        'Failed to resend code. Please try again.',
      );
      if (retryAfterSeconds && retryAfterSeconds > 0) {
        setPhoneResendCooldown(Math.ceil(retryAfterSeconds));
      }
      setStatus('phone', { tone: 'error', message });
      toast.error('Failed to Resend', { description: message });
    },
  });

  const handleEmailVerify = (e: React.FormEvent) => {
    e.preventDefault();
    if (emailCode.length === 6) {
      verifyEmailMutation.mutate();
    }
  };

  const handleCompanyEmailVerify = (e: React.FormEvent) => {
    e.preventDefault();
    if (companyEmailCode.length === 6) {
      verifyCompanyEmailMutation.mutate();
    }
  };

  const handlePhoneVerify = (e: React.FormEvent) => {
    e.preventDefault();
    if (phoneCode.length === 6) {
      verifyPhoneMutation.mutate();
    }
  };

  const isLoading =
    verifyCompanyEmailMutation.isPending ||
    verifyEmailMutation.isPending ||
    verifyPhoneMutation.isPending ||
    resendCompanyEmailMutation.isPending ||
    resendEmailMutation.isPending ||
    resendPhoneMutation.isPending;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4">
      <Card className="w-full max-w-md shadow-2xl">
        <CardHeader className="space-y-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="w-fit -ml-2 mb-2"
            disabled={isLoading}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Login
          </Button>
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-400 rounded-2xl flex items-center justify-center shadow-lg">
              <Mail className="text-white h-8 w-8" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-center">Verify Your Account</CardTitle>
          <CardDescription className="text-center">
            {requiresPhoneVerification
              ? 'Please verify your company email, admin email, and phone number to continue'
              : 'Please verify your company email and admin email to continue'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Company Email Verification Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-muted-foreground" />
                <Label className="font-medium">Company Email Verification</Label>
              </div>
              {companyEmailVerified && (
                <div className="flex items-center gap-1 text-green-600">
                  <CheckCircle2 className="h-4 w-4" />
                  <span className="text-sm">Verified</span>
                </div>
              )}
            </div>

            {!companyEmailVerified && (
              <>
                <p className="text-sm text-muted-foreground">
                  Enter the 6-digit code sent to{' '}
                  <span className="font-medium">{maskedCompanyEmail}</span>
                </p>
                <form onSubmit={handleCompanyEmailVerify} className="space-y-3">
                  <Input
                    aria-label="Company email verification code"
                    aria-invalid={hasStepError('companyEmail')}
                    aria-describedby={stepStatus.companyEmail ? companyEmailStatusId : undefined}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="Enter 6-digit code"
                    value={companyEmailCode}
                    onChange={(e) =>
                      handleCodeChange('companyEmail', e.target.value, setCompanyEmailCode)
                    }
                    disabled={isLoading}
                    className="text-center text-lg tracking-widest"
                  />
                  <StepStatusMessage id={companyEmailStatusId} status={stepStatus.companyEmail} />
                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      className="flex-1"
                      disabled={companyEmailCode.length !== 6 || isLoading}
                    >
                      {verifyCompanyEmailMutation.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        'Verify Company Email'
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      aria-label="Resend company email code"
                      onClick={() => resendCompanyEmailMutation.mutate()}
                      disabled={
                        companyEmailResendCooldown > 0 || resendCompanyEmailMutation.isPending
                      }
                    >
                      {resendCompanyEmailMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : companyEmailResendCooldown > 0 ? (
                        `${companyEmailResendCooldown}s`
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">And</span>
            </div>
          </div>

          {/* Email Verification Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-muted-foreground" />
                <Label className="font-medium">Admin Email Verification</Label>
              </div>
              {emailVerified && (
                <div className="flex items-center gap-1 text-green-600">
                  <CheckCircle2 className="h-4 w-4" />
                  <span className="text-sm">Verified</span>
                </div>
              )}
            </div>

            {!emailVerified && (
              <>
                <p className="text-sm text-muted-foreground">
                  Enter the 6-digit code sent to <span className="font-medium">{maskedEmail}</span>
                </p>
                <form onSubmit={handleEmailVerify} className="space-y-3">
                  <Input
                    aria-label="Admin email verification code"
                    aria-invalid={hasStepError('email')}
                    aria-describedby={stepStatus.email ? emailStatusId : undefined}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="Enter 6-digit code"
                    value={emailCode}
                    onChange={(e) => handleCodeChange('email', e.target.value, setEmailCode)}
                    disabled={isLoading}
                    className="text-center text-lg tracking-widest"
                  />
                  <StepStatusMessage id={emailStatusId} status={stepStatus.email} />
                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      className="flex-1"
                      disabled={emailCode.length !== 6 || isLoading}
                    >
                      {verifyEmailMutation.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        'Verify Email'
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      aria-label="Resend admin email code"
                      onClick={() => resendEmailMutation.mutate()}
                      disabled={emailResendCooldown > 0 || resendEmailMutation.isPending}
                    >
                      {resendEmailMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : emailResendCooldown > 0 ? (
                        `${emailResendCooldown}s`
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>

          {/* Divider */}
          {requiresPhoneVerification && (
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">And</span>
              </div>
            </div>
          )}

          {/* Phone Verification Section */}
          {requiresPhoneVerification && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Phone className="h-5 w-5 text-muted-foreground" />
                  <Label className="font-medium">Phone Verification</Label>
                </div>
                {phoneVerified && (
                  <div className="flex items-center gap-1 text-green-600">
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="text-sm">Verified</span>
                  </div>
                )}
              </div>

              {!phoneVerified && (
                <>
                  <p className="text-sm text-muted-foreground">
                    Enter the 6-digit code sent to{' '}
                    <span className="font-medium">{maskedPhone}</span>
                  </p>
                  <form onSubmit={handlePhoneVerify} className="space-y-3">
                    <Input
                      aria-label="Phone verification code"
                      aria-invalid={hasStepError('phone')}
                      aria-describedby={stepStatus.phone ? phoneStatusId : undefined}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      placeholder="Enter 6-digit code"
                      value={phoneCode}
                      onChange={(e) => handleCodeChange('phone', e.target.value, setPhoneCode)}
                      disabled={isLoading}
                      className="text-center text-lg tracking-widest"
                    />
                    <StepStatusMessage id={phoneStatusId} status={stepStatus.phone} />
                    <div className="flex gap-2">
                      <Button
                        type="submit"
                        className="flex-1"
                        disabled={phoneCode.length !== 6 || isLoading}
                      >
                        {verifyPhoneMutation.isPending ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Verifying...
                          </>
                        ) : (
                          'Verify Phone'
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        aria-label="Resend phone code"
                        onClick={() => resendPhoneMutation.mutate()}
                        disabled={phoneResendCooldown > 0 || resendPhoneMutation.isPending}
                      >
                        {resendPhoneMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : phoneResendCooldown > 0 ? (
                          `${phoneResendCooldown}s`
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </form>
                </>
              )}
            </div>
          )}

          {/* Progress indicator */}
          <div className="pt-4 border-t">
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <div
                className={`w-3 h-3 rounded-full ${
                  companyEmailVerified ? 'bg-green-500' : 'bg-gray-300'
                }`}
              />
              <span>Company</span>
              <div className="w-8 h-0.5 bg-gray-300" />
              <div
                className={`w-3 h-3 rounded-full ${emailVerified ? 'bg-green-500' : 'bg-gray-300'}`}
              />
              <span>Admin</span>
              {requiresPhoneVerification && (
                <>
                  <div className="w-8 h-0.5 bg-gray-300" />
                  <div
                    className={`w-3 h-3 rounded-full ${
                      phoneVerified ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  />
                  <span>Phone</span>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
